/**
 * SDK Configuration
 *
 * Configuration options for the Tonnie SDK.
 */

/** Environment type */
export type Environment = 'development' | 'staging' | 'production';

export interface SdkConfig {
  /** Base URL for the API */
  baseUrl: string;
  /** API version number */
  apiVersion?: number;
  /** Auth token for authenticated requests */
  authToken?: string;
  /** Tenant ID for multi-tenant requests */
  tenantId?: string;
  /** Callback when 401 is received */
  onUnauthorized?: () => void;
  /**
   * Global error hook — fires for any `TonnieApiError` (network,
   * timeout, 4xx, 5xx) after it's classified and BEFORE the promise
   * rejects back to the caller. Used for cross-cutting concerns like
   * Sentry capture, toast notifications, or a "can't reach server"
   * banner. The callback MUST NOT throw — if it does, the original
   * request error is still propagated unchanged.
   */
  onError?: (error: import('./base').TonnieApiError) => void;
  /** Request timeout in milliseconds */
  timeout?: number;
  /** Number of retry attempts for failed requests */
  retries?: number;
  /** Environment (development, staging, production) */
  environment?: Environment;
}

export const DEFAULT_API_VERSION = 1;
export const DEFAULT_TIMEOUT = 30000;
export const DEFAULT_RETRIES = 1;

/**
 * Get default base URL based on environment
 */
function getDefaultBaseUrl(environment?: string): string {
  switch (environment) {
    case 'production':
      return 'https://api.tonnie.ai';
    case 'staging':
      return 'https://staging.api.tonnie.ai';
    default:
      // Development fallback - production apps must provide explicit baseUrl via SdkProvider
      return 'http://localhost:4000';
  }
}

/**
 * Create SDK configuration with defaults
 */
export function createSdkConfig(config: Partial<SdkConfig> = {}): SdkConfig {
  // The caller's, never a global: `window.__TONNIE_ENV__` is the web
  // frontends' runtime configuration OBJECT (/env.js, @tonnie/env-schemas/frontend),
  // and this fallback — which nothing ever set — read it as a tier name.
  const environment = config.environment;

  return {
    baseUrl: config.baseUrl ?? getDefaultBaseUrl(environment),
    apiVersion: config.apiVersion ?? DEFAULT_API_VERSION,
    authToken: config.authToken,
    tenantId: config.tenantId,
    onUnauthorized: config.onUnauthorized,
    onError: config.onError,
    timeout: config.timeout ?? DEFAULT_TIMEOUT,
    retries: config.retries ?? DEFAULT_RETRIES,
    environment,
  };
}

/**
 * API endpoints
 */
export const API_ENDPOINTS = {
  // Auth
  AUTH: {
    ME: '/api/auth/me',
    ME_PORTALS: '/api/auth/me/portals',
    VERIFY: '/api/auth/verify',
    LOGOUT: '/api/auth/logout',
    // Password reset endpoints removed — Clerk handles this natively.
  },
  // Users
  USERS: {
    BASE: '/api/users',
    ME: '/api/users/me',
    ME_ONBOARDING_STATUS: '/api/users/me/onboarding-status',
    /**
     * W8.1 · F33 — the sixth CheckUp readiness requirement, as a read.
     */
    ME_PROFILE_COMPLETENESS: '/api/users/me/profile-completeness',
    /** The citizen's one-screen setup. */
    ME_SETUP: '/api/users/me/setup',
    /** A passport or ID card that fills the profile — read, then confirm or discard. */
    ME_IDENTITY_DOCUMENTS: '/api/users/me/identity-documents',
    ME_IDENTITY_DOCUMENT: (id: string) => `/api/users/me/identity-documents/${id}`,
    ME_IDENTITY_DOCUMENT_CONFIRM: (id: string) => `/api/users/me/identity-documents/${id}/confirm`,
  },
  // Journeys
  JOURNEYS: {
    BASE: '/api/journeys',
    BY_ID: (id: string) => `/api/journeys/${id}`,
    /** The server's progress through each hero (the citizen dashboards draw it). */
    PROGRESS: (id: string) => `/api/journeys/${id}/progress`,
    CURRENT: '/api/journeys/current',
    // ── Citizen journey management (W8.1 · F47) ─────────────────────────
    //
    // `ARCHIVED` is a separate path rather than `BASE?view=archived`: `BASE`
    // answers a bare array every journey list reads.
    ARCHIVED: '/api/journeys/archived',
    ARCHIVED_COUNT: '/api/journeys/archived/count',
    ARCHIVE: (id: string) => `/api/journeys/${id}/archive`,
    RESTORE: (id: string) => `/api/journeys/${id}/restore`,
    /** "Leave this company's programme" — ends that company's relationship (D1). */
    LEAVE_PROGRAMME: (id: string, companyTenantId: string) =>
      `/api/journeys/${id}/companies/${companyTenantId}/leave`,
    // ── L6 region: F47 · D-5 ──
    /** Choose this journey — persists `is_primary`. 404 with the flag off. */
    SELECT: (id: string) => `/api/journeys/${id}/select`,
    // ── end L6 region ──
    // ── Several journeys per route (JOURNEYS-MULTI-ROUTE) ──
    /** What the citizen already holds on a route, before building it (R4). */
    ROUTE_ADVICE: '/api/journeys/route-advice',
    /** The citizen's name for a journey (D-F). */
    LABEL: (id: string) => `/api/journeys/${id}/label`,
    // ── Per-scope consent (W8.D) ─────────────────────────────────────────
    //
    // The finer-grained sibling of `LEAVE_PROGRAMME`, which revokes a whole
    // engagement. The W2.5 routes these build on shipped with no client in any
    // app for three waves — the citizen's revoke right existed in the API and
    // was unreachable from every screen. These are the clients.
    /** Company names, scopes, grant dates and the trail. NOT flag-gated. */
    CONSENTS_DETAIL: (id: string) => `/api/journeys/${id}/consents/detail`,
    /** PUT the full resulting scope set. */
    CONSENT_SCOPES: (id: string, companyTenantId: string) =>
      `/api/journeys/${id}/consents/${companyTenantId}`,
  },
  // CheckUps (consolidated under heroes/checkup)
  CHECKUPS: {
    BASE: (talentId: string) => `/api/heroes/checkup/${talentId}`,
    HISTORY: (talentId: string) => `/api/heroes/checkup/${talentId}/history`,
    START: (talentId: string) => `/api/heroes/checkup/${talentId}/start`,
    CANCEL: (talentId: string) => `/api/heroes/checkup/${talentId}/cancel`,
    MOTIVATIONAL: (talentId: string) => `/api/heroes/checkup/${talentId}/motivational`,
    MOTIVATIONAL_DRAFT: (talentId: string) => `/api/heroes/checkup/${talentId}/motivational/draft`,
    PROGRESS: (talentId: string) => `/api/heroes/checkup/${talentId}/progress`,
    PITCH: (talentId: string) => `/api/heroes/checkup/${talentId}/pitch`,
    CALCULATE: (talentId: string) => `/api/heroes/checkup/${talentId}/calculate`,
    /**
     * Status of ONE named calculation job. The id comes from CALCULATE and is
     * persisted on the assessment row, so a client can resume watching from
     * server state after a remount. A talent-scoped status could not tell one
     * cycle's job from the next. (W5.7 · F26)
     */
    STATUS_JOB: (talentId: string, jobId: string) =>
      `/api/heroes/checkup/${talentId}/status/${jobId}`,
    RESULTS: (talentId: string) => `/api/heroes/checkup/${talentId}/results`,
    RESULTS_MESH: (talentId: string) => `/api/heroes/checkup/${talentId}/results/mesh`,
    LANGUAGE_TEST_ANALYZE: (talentId: string) =>
      `/api/heroes/checkup/${talentId}/language-test/analyze`,
  },
  TESTIT: {
    // Citizen's invited-TestIt assignment (which specific tests + done status).
    INVITATION: '/api/heroes/testit/invitation',
    RESULTS: (talentId: string) => `/api/heroes/testit/${talentId}/results`,
    HISTORY: (talentId: string) => `/api/heroes/testit/${talentId}/history`,
    RESULT_HISTORY: (talentId: string, testId: string) =>
      `/api/heroes/testit/${talentId}/results/${encodeURIComponent(testId)}/history`,
    RESULT: (talentId: string, id: string) => `/api/heroes/testit/${talentId}/results/${id}`,
    // Business (Vetting) — cross-tenant read of a candidate's results (pipeline-gated).
    VETTING_RESULTS: (talentId: string) => `/api/heroes/testit/vetting/${talentId}/results`,
    // Business (Vetting) — invited set + rich results + completion in one call.
    VETTING_OVERVIEW: (talentId: string) => `/api/heroes/testit/vetting/${talentId}/overview`,
    // Business (Vetting) — presigned pitch playback URL for the detail sheet.
    VETTING_PITCH_URL: (talentId: string) => `/api/heroes/testit/vetting/${talentId}/pitch-url`,
    // Business (Vetting) — language certificates whose latest analysis failed (W5.6).
    VETTING_FAILED_ATTEMPTS: (talentId: string) =>
      `/api/heroes/testit/vetting/${talentId}/failed-attempts`,
    // Business (Vetting) — per-test attempt history for the detail sheet.
    VETTING_RESULT_HISTORY: (talentId: string, testId: string) =>
      `/api/heroes/testit/vetting/${talentId}/results/${encodeURIComponent(testId)}/history`,
    // Business (Vetting) — nudge the candidate to complete their TestIt (429 if too soon).
    VETTING_REMIND: (talentId: string) => `/api/heroes/testit/vetting/${talentId}/remind`,
    // Business (Vetting) verdict write. The verdict READ is the evidence
    // verdict history (D3 · `EVIDENCE.VERDICT_HISTORY`).
    VERDICT: (talentId: string) => `/api/heroes/testit/vetting/${talentId}/verdict`,
  },
  // Engagement evidence (W5.8) — a company's decision records. Business-admin gated.
  EVIDENCE: {
    TALENT_CAPTURES: (talentId: string) => `/api/business/talents/${talentId}/evidence-captures`,
    VERDICT_HISTORY: (talentId: string) => `/api/business/talents/${talentId}/verdict-history`,
    CAPTURE: (captureId: string) => `/api/business/evidence-captures/${captureId}`,
    DIFF: (captureId: string) => `/api/business/evidence-captures/${captureId}/diff`,
    BLOB_URL: (captureId: string, blobRefId: string) =>
      `/api/business/evidence-captures/${captureId}/blobs/${blobRefId}/url`,
    REDACT: (captureId: string, itemId: string) =>
      `/api/business/evidence-captures/${captureId}/items/${itemId}/redact`,
  },
  // Heroes
  HEROES: {
    MILES: '/api/heroes/miles',
    PASSPORT: '/api/heroes/passport',
    PASSPORT_TIMELINE: '/api/heroes/passport/timeline',
    PASSPORT_STAMPS: '/api/heroes/passport/stamps',
    PASSPORT_STAMP: (stampId: string) => `/api/heroes/passport/stamps/${stampId}`,
    PASSPORT_CITIZEN_BADGES: '/api/heroes/passport/citizen-badges',
    PASSPORT_CITIZEN_BADGES_PROGRESS: '/api/heroes/passport/citizen-badges/progress',
    PASSPORT_JOURNEY_MILES: '/api/heroes/passport/journey-miles',
    // Business view of a specific talent's passport (RolesGuard-gated server-side)
    PASSPORT_TALENT_STAMPS: (talentId: string) => `/api/heroes/passport/${talentId}/stamps`,
    PASSPORT_TALENT_CITIZEN_BADGES: (talentId: string) =>
      `/api/heroes/passport/${talentId}/citizen-badges`,
    PASSPORT_TALENT_JOURNEY_MILES: (talentId: string) =>
      `/api/heroes/passport/${talentId}/journey-miles`,
  },
  // Business
  BUSINESS: {
    TALENTS: '/api/business/talents',
    // W7.2 · F28 — "who needs me today", ranked server-side with an i18n key
    // and a target per item. Replaces three client derivations.
    ATTENTION_QUEUE: '/api/business/attention-queue',
    PROGRAMS: '/api/business/programs',
    COHORTS: '/api/business/cohorts',
    ANALYTICS: '/api/business/analytics',
    MESSAGES_TEMPLATES: '/api/business/messages/templates',
    MESSAGES_ANALYTICS: '/api/business/messages/analytics',
    MESSAGES_SEND: '/api/business/messages/send',
    PIPELINE_SUMMARY: '/api/business/pipeline/summary',
    VETTING: '/api/business/vetting',
    MOBILITY: '/api/business/mobility',
    ADAPTATION: '/api/business/adaptation',
    CARE: '/api/business/care',
    EVIDENCE: '/api/business/evidence',
    POOL: '/api/business/pool',
    DASHBOARD: '/api/business/dashboard',
    ACTIVITY: '/api/business/activity',
    // Affiliate program (BL-213)
    AFFILIATE_STATUS: '/api/business/affiliate/status',
    AFFILIATE_OVERVIEW: '/api/business/affiliate/overview',
    // Tonnie-wide cross-tenant overview (B10) — internal-tenant gated + audit-logged.
    AFFILIATE_PLATFORM_OVERVIEW: '/api/billing/affiliates/platform/overview',
    AFFILIATE_REDEMPTIONS: '/api/business/affiliate/redemptions',
    AFFILIATE_REDEMPTIONS_CSV: '/api/business/affiliate/redemptions.csv',
    AFFILIATE_PAYOUTS: '/api/business/affiliate/payouts',
    AFFILIATE_PAYOUT_CANCEL: (id: string) => `/api/business/affiliate/payouts/${id}/cancel`,
    AFFILIATE_PAYOUT_INVOICE_URL: (id: string) =>
      `/api/business/affiliate/payouts/${id}/invoice-url`,
    AFFILIATE_PAYOUT_INVOICE: (id: string) => `/api/business/affiliate/payouts/${id}/invoice`,
    // Invitation-first acquisition flow (Phase 5+6)
    INVITATIONS: '/api/business/invitations',
    INVITATION_BY_ID: (id: string) => `/api/business/invitations/${id}`,
    // Cohort / program snapshot — the machine-readable contract.
    //
    // The cohort summary CSV, programme snapshot and programme report/summary
    // CSV routes have no constant here: no SDK method, hook or screen reaches
    // them. Their UI path (H12) is deferred to the next hotfix by the owner
    // (4 Oct 2026, decisions register FR-12 in docs/01-platform/_deprecated/2026-10/HOTFIX-2026.9.14.md); the
    // routes stay, and their constants come back with the screens that call them.
    COHORT_SNAPSHOT: (id: string) => `/api/business/cohorts/${id}/snapshot`,
    COHORT_REPORT_CSV: (id: string) => `/api/business/cohorts/${id}/report.csv`,
    // Service credentials (admin-only; a credential cannot manage credentials).
    SERVICE_CREDENTIALS: '/api/business/service-credentials',
    SERVICE_CREDENTIAL_REVOKE: (id: string) => `/api/business/service-credentials/${id}/revoke`,
    // Journey-scoped archive lifecycle. Addressed by JOURNEY id, never by
    // talent id: one talent can hold several journeys in the same tenant
    // (re-invitation, multi-opportunity), so a talent-scoped archive route
    // cannot say WHICH card the admin clicked.
    JOURNEY_ARCHIVE: (journeyId: string) => `/api/business/journeys/${journeyId}/archive`,
    JOURNEY_UNARCHIVE: (journeyId: string) => `/api/business/journeys/${journeyId}/unarchive`,
    JOURNEYS_BULK_ARCHIVE: '/api/business/journeys/bulk-archive',
    INVITATION_BULK: '/api/business/invitations/bulk',
    INVITATION_SEND: (id: string) => `/api/business/invitations/${id}/send`,
    INVITATION_RESEND: (id: string) => `/api/business/invitations/${id}/resend`,
    INVITATION_REVOKE: (id: string) => `/api/business/invitations/${id}/revoke`,
  },
  // Talent invitations (citizen-facing public preview + authed accept)
  TALENT_INVITATIONS: {
    PREVIEW: '/api/talent-invitations/preview',
    ELIGIBILITY: '/api/talent-invitations/eligibility',
    MINE: '/api/talent-invitations/mine',
    DEFER: '/api/talent-invitations/defer',
    ACCEPT: '/api/talent-invitations/accept',
    /** Which journey the company is on — one the citizen holds, or a new one (R5)? Before Accept. */
    ACCEPT_PREVIEW: '/api/talent-invitations/accept-preview',
    DECLINE: '/api/talent-invitations/decline',
    /** Citizen first run · S5 — claim an invitation sent to another account. */
    CLAIM_START: '/api/talent-invitations/claim/start',
    CLAIM_VERIFY: '/api/talent-invitations/claim/verify',
  },
  // Unsubscribe — reached by a recipient with NO session. The token in the
  // query is the credential (see `users.unsubscribe_token`, migration 0107).
  // GET previews and POST performs, deliberately: mail scanners fetch GET
  // links unattended and must not be able to opt anyone out.
  // The Google Places proxy — the key never leaves the server.
  INTEGRATIONS: {
    PLACES_AUTOCOMPLETE: '/api/integrations/places/autocomplete',
  },
  UNSUBSCRIBE: {
    ROOT: '/api/public/unsubscribe',
  },
  // Family (planned - controller not yet implemented)
  FAMILY: {
    MEMBERS: '/api/family/members',
    BY_ID: (id: string) => `/api/family/members/${id}`,
    DOCUMENTS: (memberId: string) => `/api/family/members/${memberId}/documents`,
    INVITE: '/api/family/invite',
  },
  // Care
  CARE: {
    PILLARS: (talentId: string) => `/api/heroes/care/${talentId}/pillars`,
    CHECKPOINTS: (talentId: string) => `/api/heroes/care/${talentId}/checkpoints`,
    CHECKPOINT: (talentId: string, checkpointId: string) =>
      `/api/heroes/care/${talentId}/checkpoints/${checkpointId}`,
    CHECKPOINT_SUBMIT: (talentId: string, checkpointId: string) =>
      `/api/heroes/care/${talentId}/checkpoints/${checkpointId}/submit`,
    CHECKIN_MOOD: (talentId: string) => `/api/heroes/care/${talentId}/checkin/mood`,
    CHECKIN_QUICK: (talentId: string) => `/api/heroes/care/${talentId}/checkin/quick`,
    CHECKINS: (talentId: string) => `/api/heroes/care/${talentId}/checkins`,
    STRATEGY: (talentId: string) => `/api/heroes/care/${talentId}/strategy`,
    WELLBEING_SUMMARY: (talentId: string) => `/api/heroes/care/${talentId}/wellbeing-summary`,
    MOMENTUM: (talentId: string) => `/api/heroes/care/${talentId}/momentum`,
    INTERACTIONS: (talentId: string) => `/api/heroes/care/${talentId}/interactions`,
    SUPPORT: (talentId: string) => `/api/heroes/care/${talentId}/support`,
    SUPPORT_DETAIL: (talentId: string, requestId: string) =>
      `/api/heroes/care/${talentId}/support/${requestId}`,
    SCHEDULE: (talentId: string) => `/api/heroes/care/${talentId}/schedule`,
    // Objectives-based feedback (BL-125) — citizen view + self-evaluation.
    OBJECTIVES: (talentId: string) => `/api/heroes/care/${talentId}/objectives`,
    OBJECTIVE_SELF_EVAL: (talentId: string) =>
      `/api/heroes/care/${talentId}/objective-feedback/self-eval`,
  },
  // Onboarding
  ONBOARDING: {
    EXTRACT_PROFILE: '/api/onboarding/extract-profile',
  },
  // OAuth (planned - controller not yet implemented)
  OAUTH: {
    LINKEDIN: '/api/oauth/linkedin',
    LINKEDIN_CALLBACK: '/api/oauth/linkedin/callback',
  },
  // TelePortal
  TELEPORTAL: {
    MILESTONES: (journeyId: string) => `/api/heroes/teleportal/${journeyId}/milestones`,
    TRACKS: (journeyId: string) => `/api/heroes/teleportal/${journeyId}/tracks`,
    TRACK: (journeyId: string, trackType: string) =>
      `/api/heroes/teleportal/${journeyId}/tracks/${trackType}`,
    TASKS: (journeyId: string) => `/api/heroes/teleportal/${journeyId}/tasks`,
    TASK: (journeyId: string, taskId: string) =>
      `/api/heroes/teleportal/${journeyId}/tasks/${taskId}`,
    TASK_COMPLETE: (journeyId: string, taskId: string) =>
      `/api/heroes/teleportal/${journeyId}/tasks/${taskId}/complete`,
    DOCUMENTS: (journeyId: string) => `/api/heroes/teleportal/${journeyId}/documents`,
    DOCUMENT_VALIDATE: (journeyId: string, docId: string) =>
      `/api/heroes/teleportal/${journeyId}/documents/${docId}/validate`,
    DOCUMENT: (journeyId: string, documentId: string) =>
      `/api/heroes/teleportal/${journeyId}/documents/${documentId}`,
    DOCUMENT_DOWNLOAD: (journeyId: string, documentId: string) =>
      `/api/heroes/teleportal/${journeyId}/documents/${documentId}/download`,
    DOCUMENTS_DOWNLOAD_ZIP: (journeyId: string) =>
      `/api/heroes/teleportal/${journeyId}/documents/download-zip`,
    PACKAGES: (journeyId: string) => `/api/heroes/teleportal/${journeyId}/packages`,
    PACKAGE: (journeyId: string, packageId: string) =>
      `/api/heroes/teleportal/${journeyId}/packages/${packageId}`,
    PACKAGE_SHARE: (journeyId: string, packageId: string) =>
      `/api/heroes/teleportal/${journeyId}/packages/${packageId}/share`,
    SETUP: (journeyId: string) => `/api/heroes/teleportal/${journeyId}/setup`,
    SETUP_INSTANTIATE: (journeyId: string) =>
      `/api/heroes/teleportal/${journeyId}/setup/instantiate`,
    SETUP_ACTIVATE: (journeyId: string) => `/api/heroes/teleportal/${journeyId}/setup/activate`,
    SETUP_REFRESH: (journeyId: string) => `/api/heroes/teleportal/${journeyId}/setup/refresh`,
    SETUP_PRE_POPULATE: (journeyId: string) =>
      `/api/heroes/teleportal/${journeyId}/setup/pre-populate`,
    /** F38 — the visa pathways the catalog offers for this journey's destination. */
    SETUP_VISA_PATHWAYS: (journeyId: string) =>
      `/api/heroes/teleportal/${journeyId}/setup/visa-pathways`,
    HOUSEHOLD_MEMBERS: (journeyId: string) =>
      `/api/heroes/teleportal/${journeyId}/household-members`,
    HOUSEHOLD_MEMBER: (journeyId: string, memberId: string) =>
      `/api/heroes/teleportal/${journeyId}/household-members/${memberId}`,
    /** F35 · D-17 — the household step's prefill: roster, else onboarding, else none. */
    HOUSEHOLD_PROPOSAL: (journeyId: string) =>
      `/api/heroes/teleportal/${journeyId}/household-proposal`,
    TASK_REOPEN: (journeyId: string, taskId: string) =>
      `/api/heroes/teleportal/${journeyId}/tasks/${taskId}/reopen`,
    DOCUMENT_VISION: (journeyId: string, documentId: string) =>
      `/api/heroes/teleportal/${journeyId}/documents/${documentId}/vision-result`,
  },
  // Brain Setup Orchestration
  BRAIN_SETUP: {
    PLAN: '/api/brain/reasoning/teleportal/setup/plan',
    NEXT: '/api/brain/reasoning/teleportal/setup/next',
    VALIDATE: '/api/brain/reasoning/teleportal/setup/validate',
  },
  // QultureUp
  QULTUREUP: {
    PROGRESS: '/api/heroes/qultureup/progress',
    MISSIONS: '/api/heroes/qultureup/missions',
    MISSION: (missionId: string) => `/api/heroes/qultureup/missions/${missionId}`,
    WORLDS: '/api/heroes/qultureup/worlds',
    WORLD: (worldId: string) => `/api/heroes/qultureup/worlds/${worldId}`,
  },
} as const;
